import React, { useState } from 'react';
import SidebarNav from '../components/SidebarNav';
import { Menu } from 'lucide-react';
import './PrivacyPolicyPage.css';

const PrivacyPolicyPage = ({ currentPage, onNavigate, onSignOut, userProfile, onShowAuth }) => {
    const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

    return (
        <div className="privacy-layout">
            <SidebarNav 
                currentPage={currentPage} 
                onNavigate={onNavigate}
                onSignOut={onSignOut}
                isMobileOpen={isMobileNavOpen}
                onCloseMobile={() => setIsMobileNavOpen(false)}
                userProfile={userProfile}
                onShowAuth={onShowAuth}
            />

            <main className="privacy-main">
                <div className="privacy-container">
                    <header className="privacy-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <button 
                                className="mobile-menu-btn"
                                onClick={() => setIsMobileNavOpen(true)}
                            >
                                <Menu size={24} />
                            </button>
                            <h1>SpotLogs Privacy Policy</h1>
                        </div>
                        <p>Last Updated: January 22, 2026</p>
                    </header>

                    <div className="privacy-content glass-card">
                        <section className="policy-section">
                            <h2>1. Introduction</h2>
                            <p>SpotLogs ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your information when you use our trip planning and ELD log generation service.</p>
                            <p>SpotLogs is a tool designed to help truck drivers plan compliant routes and generate daily log sheets based on FMCSA Hours of Service regulations.</p>
                        </section>

                        <section className="policy-section">
                            <h2>2. Information We Collect</h2>
                            <h3>2.1 Information You Provide</h3>
                            <p>When you use SpotLogs, you provide:</p>
                            <ul>
                                <li><strong>Profile Information:</strong> Driver name, vehicle unit number, carrier name, truck plate/license (optional), DOT number (optional).</li>
                                <li><strong>Trip Details:</strong> Current location, pickup location, dropoff location, current cycle hours used.</li>
                            </ul>

                            <h3>2.2 Automatically Collected Information</h3>
                            <p>When you use our service, we may automatically collect:</p>
                            <ul>
                                <li><strong>Device Information:</strong> Browser type and version, operating system, device type (mobile/tablet/desktop), screen resolution.</li>
                                <li><strong>Usage Information:</strong> Pages visited, features used, time spent on platform, actions taken (route generation, log downloads, etc.).</li>
                                <li><strong>Technical Information:</strong> IP address (anonymized), timestamps of activities, error logs (for debugging).</li>
                            </ul>

                            <h3>2.3 Generated Data</h3>
                            <p>Based on your inputs, we generate:</p>
                            <ul>
                                <li>Route information (map coordinates, stop locations, estimated times)</li>
                                <li>Daily log sheets (duty status grids, compliance calculations)</li>
                                <li>Mileage calculations</li>
                                <li>Rest/break schedules</li>
                            </ul>
                        </section>

                        <section className="policy-section">
                            <h2>3. How We Store Your Information</h2>
                            <h3>3.1 Local Storage</h3>
                            <p>Your profile information (driver name, vehicle number, carrier name, etc.) is stored locally on your device using browser localStorage. This means:</p>
                            <ul>
                                <li>We do NOT store this data on our servers</li>
                                <li>Data persists only on your device</li>
                                <li>Clearing browser data will delete this information</li>
                                <li>Other users on different devices cannot access your profile data</li>
                            </ul>

                            <h3>3.2 Server Storage</h3>
                            <p>Trip details and generated logs are temporarily stored on our servers for:</p>
                            <ul>
                                <li>Processing route calculations</li>
                                <li>Generating log sheets</li>
                                <li>Delivering results to you</li>
                            </ul>
                            <p><strong>Retention Period:</strong></p>
                            <ul>
                                <li>Trip data: 30 days after generation</li>
                                <li>Generated logs: 30 days after creation</li>
                                <li>Usage analytics: 90 days (anonymized)</li>
                            </ul>
                            <p>After these periods, data is permanently deleted from our servers.</p>
                        </section>

                        <section className="policy-section">
                            <h2>4. How We Use Your Information</h2>
                            <p>We use collected information to:</p>
                            <h3>4.1 Provide Services</h3>
                            <ul>
                                <li>Calculate compliant routes based on HOS regulations</li>
                                <li>Generate accurate daily log sheets</li>
                                <li>Display trip information on maps</li>
                                <li>Auto-fill log headers with your profile data</li>
                            </ul>
                            <h3>4.2 Improve Services</h3>
                            <ul>
                                <li>Identify and fix bugs</li>
                                <li>Optimize route calculations</li>
                                <li>Enhance user interface/experience</li>
                                <li>Develop new features</li>
                            </ul>
                            <h3>4.3 Analytics (Anonymized)</h3>
                            <ul>
                                <li>Understand feature usage patterns</li>
                                <li>Measure platform performance</li>
                                <li>Identify areas for improvement</li>
                            </ul>
                            <p>Note: Analytics data is anonymized and cannot be traced back to individual users.</p>
                        </section>

                        <section className="policy-section">
                            <h2>5. Information Sharing & Disclosure</h2>
                            <h3>5.1 We Do NOT Sell Your Data</h3>
                            <p>We do not sell, rent, or trade your personal information to third parties for marketing purposes.</p>

                            <h3>5.2 Third-Party Services</h3>
                            <p>We use the following third-party services to operate SpotLogs:</p>
                            <ul>
                                <li><strong>OpenStreetMap (OSM):</strong> Provides map tiles and routing data. (Privacy Policy: openstreetmap.org/copyright)</li>
                                <li><strong>Leaflet.js:</strong> Open-source mapping library. Runs entirely in your browser. No data sent to third parties.</li>
                                <li><strong>Vercel (Hosting):</strong> Frontend hosting provider. (Privacy Policy: vercel.com/legal/privacy-policy)</li>
                                <li><strong>Render (Hosting):</strong> Backend API hosting. (Privacy Policy: render.com/privacy)</li>
                            </ul>

                            <h3>5.3 Legal Requirements</h3>
                            <p>We may disclose information if required by law or in response to: valid legal processes (subpoenas, court orders), compliance with regulations, protection of our rights or safety, prevention of fraud or abuse.</p>
                        </section>

                        <section className="policy-section">
                            <h2>6. Data Security</h2>
                            <p>We implement industry-standard security measures to protect your information:</p>
                            <ul>
                                <li><strong>HTTPS Encryption:</strong> All data transmitted between your device and our servers is encrypted using SSL/TLS.</li>
                                <li><strong>Secure Servers:</strong> Backend infrastructure hosted on secure cloud platforms with regular security updates.</li>
                                <li><strong>Access Controls:</strong> Limited employee access to systems containing user data.</li>
                                <li><strong>Regular Audits:</strong> Periodic security reviews and vulnerability assessments.</li>
                            </ul>
                            <p>Note: While we take reasonable precautions, no method of transmission over the internet is 100% secure.</p>
                        </section>

                        <section className="policy-section">
                            <h2>7. Your Rights & Choices</h2>
                            <p>You have the following rights:</p>
                            <ul>
                                <li><strong>Access & Update:</strong> View and update your profile information anytime in Settings.</li>
                                <li><strong>Delete Your Data:</strong> Local Data: Clear browser data or use "Reset App Data" in Settings. Server Data: Contact us to request deletion of trip/log data.</li>
                                <li><strong>Export Your Data:</strong> Download generated log sheets as PDFs. Request a copy of stored trip data by emailing privacy@spotlogs.com.</li>
                                <li><strong>Opt-Out of Analytics:</strong> Enable "Do Not Track" in your browser. We will honor DNT signals and not collect usage analytics.</li>
                            </ul>
                        </section>

                        <section className="policy-section">
                            <h2>8. Cookies & Tracking Technologies</h2>
                            <p>SpotLogs uses minimal cookies:</p>
                            <ul>
                                <li><strong>Session Cookies:</strong> Maintain your login state during active session (deleted when browser closes).</li>
                                <li><strong>Preference Cookies:</strong> Remember your theme (dark/light mode) and other UI preferences.</li>
                            </ul>
                            <p>We do NOT use: Third-party advertising cookies, Cross-site tracking cookies, Social media tracking pixels.</p>
                            <p>You can disable cookies in your browser settings, but this may limit functionality.</p>
                        </section>

                        <section className="policy-section">
                            <h2>9. Children's Privacy</h2>
                            <p>SpotLogs is not intended for use by individuals under 18 years of age. We do not knowingly collect information from minors. If you are under 18, please do not use this service.</p>
                        </section>

                        <section className="policy-section">
                            <h2>10. International Users</h2>
                            <p>SpotLogs is based in the United States. If you access our service from outside the U.S., your information may be transferred to and processed in the U.S. where data protection laws may differ from your country. By using SpotLogs, you consent to this transfer and processing.</p>
                        </section>

                        <section className="policy-section">
                            <h2>11. Changes to This Policy</h2>
                            <p>We may update this Privacy Policy periodically. Changes will be posted on this page with an updated "Last Updated" date.</p>
                            <p>Material changes will be communicated via: In-app notification banner, Email (if we have your contact info).</p>
                            <p>Continued use of SpotLogs after changes constitutes acceptance of the updated policy.</p>
                        </section>

                        <section className="policy-section">
                            <h2>12. Disclaimer</h2>
                            <p>SpotLogs is a trip planning tool and does NOT replace certified Electronic Logging Devices (ELDs) required by FMCSA regulations (49 CFR Part 395).</p>
                            <p>Generated logs are for planning purposes only. Drivers are responsible for: Maintaining accurate, real-time records using compliant ELDs, Verifying route calculations, Ensuring compliance with all HOS regulations.</p>
                            <p>SpotLogs is not liable for violations resulting from reliance on generated logs or route plans.</p>
                        </section>

                         <section className="policy-section contact-section">
                            <h2>13. Contact Us</h2>
                            <p>For questions or concerns about this Privacy Policy or your data:</p>
                            <ul>
                                <li>📧 Email: privacy@spotlogs.com</li>
                                <li>📞 Phone: 1-800-SPOTLOG (1-800-776-8564)</li>
                            </ul>
                            <p><strong>Mailing Address:</strong><br/>
                            SpotLogs Privacy Team<br/>
                            [Your Company Address]<br/>
                            [City, State, ZIP]</p>
                            <p>We will respond to privacy inquiries within 30 days.</p>
                        </section>

                        <div className="policy-footer">
                            <h3>Official FMCSA HOS Guide</h3>
                            <a href="https://www.fmcsa.dot.gov/regulations/hours-service" target="_blank" rel="noopener noreferrer" className="download-link">
                                <span className="material-symbols-outlined">description</span>
                                Interstate Truck Driver's Guide to Hours of Service (PDF)
                            </a>
                            <p className="footer-note">This document is provided by the Federal Motor Carrier Safety Administration and is the authoritative source for HOS regulations.</p>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default PrivacyPolicyPage;
